import "./Cart.scss";
import { useNavigate } from "react-router-dom";
import returnIcon from "./Assets/returnIcon.svg";
import rupeesIcon from "./Assets/rupeesIcon.svg";
import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import selectSvg from './Assets/select.svg';
import unselectSvg from './Assets/unselect.svg';
import { PageLoadLoader } from "../Components/Loader";
import deliveryIcon from './Assets/delivery.svg';
import { useProfile } from "../Contexts/ProfileContext";
import { useToast } from "../Contexts/ToastContext";
import { showOrderErrorToast } from "../utils/orderErrorMessage";
import { beginCheckoutSession } from "../utils/orderSuccessSnapshot";
import { allowCheckoutStepTransition } from "../hooks/useMobCheckoutLeaveConfirm";
import { fetchCoupons, resolvePendingCoupon } from "../utils/couponsApi";
import { getPreferredNotifyChannel } from "../utils/notifyChannel";
import {
  getCartLineMaxStock,
  getSelectedSizeStock,
  getCartQuantityOptions,
  nextCartQuantity,
  clampQtyToStock,
  partitionCartByStock,
} from "./cartQuantity";

export function Cart() {

  const [cartData, setCartData] = useState([]);
  const [selectedCarts, setSelectedCarts] = useState({});
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);

  const [selectedSize, setSelectedSize] = useState({});
  const [selectedQty, setSelectedQty] = useState({});
  const [availableSizes, setAvailableSizes] = useState({});
  console.log('available sizes - ', availableSizes);
  
  const [availableQuantities, setAvailableQuantities] = useState({});
  const [sizeDropdowns, setSizeDropdowns] = useState([]);
  const [qtyDropdowns, setQtyDropdowns] = useState([]);
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState(null);
  const [dropdownPositions, setDropdownPositions] = useState({});
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { fetchProfile } = useProfile();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchCoupons();
        if (cancelled) return;
        setAvailableCoupons(list);
        const pending = await resolvePendingCoupon(list);
        if (pending && !cancelled) setSelectedCoupon(pending);
      } catch (e) {
        console.error('Failed to load coupons:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCouponSelect = (coupon) => {
    setSelectedCoupon(coupon);
    setShowCouponModal(false);
  };

  const handleRemoveCoupon = () => {
    setSelectedCoupon(null);
  };

  const handleDropdownToggle = (index, cond, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (cond === "SIZE") {
      let sizeDropdownsCopy = [...sizeDropdowns];
      const isOpening = !sizeDropdownsCopy[index];
      sizeDropdownsCopy[index] = isOpening;
      // Close all other dropdowns
      sizeDropdownsCopy = sizeDropdownsCopy.map((val, i) => i === index ? sizeDropdownsCopy[index] : false);
      setSizeDropdowns(sizeDropdownsCopy);
      
      // Calculate position for fixed positioning
      if (isOpening && event) {
        const button = event.currentTarget;
        requestAnimationFrame(() => {
          const rect = button.getBoundingClientRect();
          setDropdownPositions(prev => ({
            ...prev,
            [index]: {
              top: rect.bottom + 4,
              left: rect.left,
              width: rect.width
            }
          }));
        });
      } else {
        // Clear position when closing
        setDropdownPositions(prev => {
          const newPositions = { ...prev };
          delete newPositions[index];
          return newPositions;
        });
      }
    }
    else {
      let qtyDropdownsCopy = [...qtyDropdowns];
      qtyDropdownsCopy[index] = !qtyDropdownsCopy[index];
      setQtyDropdowns(qtyDropdownsCopy);
    }
  };

  const handleSizeOrQtySelect = async (size_or_qty, index, cond) => {
    if (cond == "SIZE") {
      const cartItem = cartData[index];
      const selectedSizeStock = cartItem?.size?.[size_or_qty] ?? 0;
      if (selectedSizeStock <= 0) {
        let sizeDropdownsCopy = [...sizeDropdowns];
        sizeDropdownsCopy[index] = false;
        setSizeDropdowns(sizeDropdownsCopy);
        await removeCartItem(index);
        return;
      }
      setSelectedSize({ ...selectedSize, [index]: size_or_qty });
      const maxQuantity = getCartLineMaxStock(cartItem, size_or_qty);
      const availableQuantitiesForProduct = getCartQuantityOptions(cartItem, size_or_qty);
      setAvailableQuantities((prev) => ({
        ...prev,
        [index]: availableQuantitiesForProduct,
      }));
      const currentQty = selectedQty[index] || 1;
      if (currentQty > maxQuantity) {
        setSelectedQty({ ...selectedQty, [index]: clampQtyToStock(currentQty, maxQuantity) });
      }
      let sizeDropdownsCopy = [...sizeDropdowns];
      sizeDropdownsCopy[index] = false;
      setSizeDropdowns(sizeDropdownsCopy);
    }
    else {
      setSelectedQty({ ...selectedQty, [index]: size_or_qty });
      let qtyDropdownsCopy = [...qtyDropdowns];
      qtyDropdownsCopy[index] = false;
      setQtyDropdowns(qtyDropdownsCopy);
    }
  };

  const handleQuantityChange = (index, change) => {
    const maxStock = getCartLineMaxStock(cartData[index]);
    const { nextQty, removeItem } = nextCartQuantity(selectedQty?.[index], change, maxStock);
    if (removeItem) {
      setConfirmRemoveIndex(index);
      return;
    }
    setSelectedQty({ ...selectedQty, [index]: nextQty });
  };

  const handleMinusClick = (index) => {
    const currentQty = selectedQty?.[index] || 1;
    if (currentQty <= 1) {
      setConfirmRemoveIndex(index);
      return;
    }
    handleQuantityChange(index, -1);
  };

  // Remove item from cart (confirmation-approved)
  const removeCartItem = async (index) => {
    try {
      const isGuest = localStorage.getItem('guestUser') === 'true';
      if (isGuest) {
        const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
        guestCart.splice(index, 1);
        localStorage.setItem('guestCart', JSON.stringify(guestCart));
        window.dispatchEvent(new CustomEvent('guestCartUpdated'));
        await fetchCarts();
        setConfirmRemoveIndex(null);
        return;
      }

      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      const cartId = cartData[index]?._id;
      if (!cartId) {
        console.error('Cart ID not found for index:', index);
        showToast({ message: 'Could not find cart item to delete' });
        setConfirmRemoveIndex(null);
        return;
      }

      const response = await axios.delete(
        `/cart/delete`,
        {
          data: { cartId },
          headers: config.headers
        }
      );

      console.log('Cart item deleted:', response.data);
      
      await fetchCarts();
      try {
        await fetchProfile();
      } catch (profileError) {
        console.error('Failed to refetch profile data:', profileError);
      }
      
    } catch (error) {
      console.error('Failed to remove cart item:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to remove item from cart';
      showToast({ message: errorMessage });
    } finally {
      setConfirmRemoveIndex(null);
    }
  };

  async function fetchCarts() {
    setLoading(true);
    try {
      const isGuest = localStorage.getItem('guestUser') === 'true';
      if (isGuest) {
        const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
        const { inStock: guestInStock, outOfStock: guestOos } = partitionCartByStock(guestCart);
        if (guestOos.length > 0) {
          localStorage.setItem('guestCart', JSON.stringify(guestInStock));
          window.dispatchEvent(new CustomEvent('guestCartUpdated'));
          showToast({
            message:
              guestOos.length === 1
                ? 'An out-of-stock item was removed from your cart.'
                : `${guestOos.length} out-of-stock items were removed from your cart.`,
          });
        }
        const processedData = guestInStock.map((cartItem) => {
          const productSizes = cartItem.size || {};
          const availableSizesForProduct = Object.keys(productSizes).filter(size => productSizes[size] > 0);
          const selectedSizeStock = getSelectedSizeStock(cartItem);
          const availableQuantitiesForProduct = getCartQuantityOptions(cartItem);
          return {
            ...cartItem,
            availableSizes: availableSizesForProduct,
            availableQuantities: availableQuantitiesForProduct.length ? availableQuantitiesForProduct : [1],
            maxStock: selectedSizeStock
          };
        });
        setCartData(processedData);
        const cartsObj = {};
        const sizesObj = {};
        const quantitiesObj = {};
        const availableSizesObj = {};
        const availableQuantitiesObj = {};
        processedData.forEach((cart, index) => {
          cartsObj[index] = true;
          sizesObj[index] = cart.selectedSize;
          quantitiesObj[index] = clampQtyToStock(cart.selectedQty, getCartLineMaxStock(cart));
          availableSizesObj[index] = cart.availableSizes;
          availableQuantitiesObj[index] = cart.availableQuantities;
        });
        setSelectedCarts(cartsObj);
        setSelectedSize(sizesObj);
        setSelectedQty(quantitiesObj);
        setAvailableSizes(availableSizesObj);
        setAvailableQuantities(availableQuantitiesObj);
        setSizeDropdowns(new Array(processedData.length).fill(false));
        setQtyDropdowns(new Array(processedData.length).fill(false));
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(
        `/cart/get-all`,
        config
      );

      let data = response.data;
      console.log('fetchCarts response data - ', data);

      const cartList = Array.isArray(data) ? data : [];
      const { inStock, outOfStock } = partitionCartByStock(cartList);

      if (outOfStock.length > 0) {
        await Promise.all(
          outOfStock.map((item) =>
            item._id
              ? axios.delete('/cart/delete', {
                  data: { cartId: item._id },
                  headers: config.headers,
                })
              : Promise.resolve()
          )
        );
        try {
          await fetchProfile();
        } catch (profileError) {
          console.error('Failed to refetch profile after OOS removal:', profileError);
        }
        showToast({
          message:
            outOfStock.length === 1
              ? 'An out-of-stock item was removed from your cart.'
              : `${outOfStock.length} out-of-stock items were removed from your cart.`,
        });
      }

      // Process cart data to include available sizes and quantities from product data
      const processedData = inStock.map((cartItem) => {
        const productSizes = cartItem.size || {};
        const availableSizesForProduct = Object.keys(productSizes).filter(size => productSizes[size] > 0);
        const selectedSizeStock = getSelectedSizeStock(cartItem);
        const availableQuantitiesForProduct = getCartQuantityOptions(cartItem);

        return {
          ...cartItem,
          availableSizes: availableSizesForProduct,
          availableQuantities: availableQuantitiesForProduct.length ? availableQuantitiesForProduct : [1],
          maxStock: selectedSizeStock
        };
      });

      setCartData(processedData);

      // Initialize selected states with actual cart data
      let cartsObj = {}
      let sizesObj = {}
      let quantitiesObj = {}
      let availableSizesObj = {}
      let availableQuantitiesObj = {}

      processedData.forEach((cart, index) => {
        cartsObj[index] = true
        
        // Use actual selected size and quantity from cart data
        sizesObj[index] = cart.selectedSize;
        quantitiesObj[index] = clampQtyToStock(cart.selectedQty, getCartLineMaxStock(cart));
        availableSizesObj[index] = cart.availableSizes;
        availableQuantitiesObj[index] = cart.availableQuantities;
      })

      setSelectedCarts(cartsObj)
      setSelectedSize(sizesObj)
      setSelectedQty(quantitiesObj)
      setAvailableSizes(availableSizesObj)
      setAvailableQuantities(availableQuantitiesObj)
      setSizeDropdowns(new Array(processedData.length).fill(false))
      setQtyDropdowns(new Array(processedData.length).fill(false))

      console.log('cartsObj - ', cartsObj);
    }
    catch (error) {
      console.error("Failed to fetch products:", error);
    }
    finally {
      setLoading(false);
    }
  }

  function updateSelectedCarts(index) {
    console.log('in updateSelectedCarts - ', index);

    setSelectedCarts({ ...selectedCarts, [index]: !selectedCarts[index] })
  }

  async function placeOrder() {
    console.log('in placeOrder');
    if (placingOrder) return;

    const isGuest = localStorage.getItem('guestUser') === 'true';

    const orderItems = []
    let totalMRP = 0
    let totalDiscount = 0
    let totalAmount = 0
    let itemCount = 0

    Object.keys(selectedCarts).forEach((key) => {
      if (selectedCarts[key]) {
        const cartItem = cartData[key]
        const quantity = selectedQty[key]
        const size = selectedSize[key]
        const orderItem = {
          product_id: cartItem.product_id,
          selectedSize: size,
          selectedQty: quantity,
          unit_price: cartItem.price,
          unit_mrp: cartItem.mrp,
          total_price: cartItem.price * quantity,
          total_mrp: cartItem.mrp * quantity,
          discount_amount: (cartItem.mrp - cartItem.price) * quantity,
          product_title: cartItem.title,
          product_data: { title: cartItem.title, urls: cartItem.urls || [] }
        }
        orderItems.push(orderItem)
        totalMRP += cartItem.mrp * quantity
        totalDiscount += (cartItem.mrp - cartItem.price) * quantity
        totalAmount += cartItem.price * quantity
        itemCount += quantity
      }
    })

    if (orderItems.length === 0) {
      showToast({ message: 'Select at least one item to place order.' });
      return;
    }

    let couponDiscountAmount = 0;
    if (selectedCoupon) {
      if (selectedCoupon.type === 'percentage') {
        couponDiscountAmount = Math.round(totalAmount * selectedCoupon.discount / 100);
      } else {
        couponDiscountAmount = selectedCoupon.discount;
      }
    }
    const finalAmount = totalAmount - couponDiscountAmount

    const notification_channel = getPreferredNotifyChannel();
    const orderData = {
      order_items: orderItems,
      order_summary: {
        item_count: itemCount,
        total_mrp: totalMRP,
        total_discount: totalDiscount,
        coupon_discount: couponDiscountAmount,
        shipping_fee: 0,
        final_amount: finalAmount
      },
      coupon_code: selectedCoupon ? selectedCoupon.code : '',
      use_store_credit: false,
      ...(notification_channel && { notification_channel }),
    }

    setPlacingOrder(true);

    if (isGuest) {
      try {
        const guestCheckoutData = { ...orderData, selectedCoupon: selectedCoupon || null };
        localStorage.setItem('guestCheckoutData', JSON.stringify(guestCheckoutData));
        beginCheckoutSession();
        allowCheckoutStepTransition();
        navigate('/order-summary');
      } finally {
        setPlacingOrder(false);
      }
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      console.log('Comprehensive order data:', orderData);

      const response = await axios.post(
        `/orders/create`,
        orderData,
        config
      );

      let data = response.data;
      console.log('Order creation response:', data);

      // Backend returns only order header (no order_items). Save order summary page data with our order_items (they include product_data for images).
      if (data.order) {
        const orderToSave = {
          ...data.order,
          order_items: orderData.order_items || []
        };
        localStorage.setItem('latestOrder', JSON.stringify(orderToSave));
      }

      // Navigate only on success
      beginCheckoutSession();
      allowCheckoutStepTransition();
      navigate('/order-summary');

    }
    catch (error) {
      console.log('Error creating order:', error);
      console.log('Error details:', error.response?.data);
      showOrderErrorToast(showToast, error, 'Failed to place order. Please try again.');
    }
    finally {
      setPlacingOrder(false);
    }

  }

  function couponDiscount() {
    if (!selectedCoupon) return 0;
    
    const total = cartData.reduce((prevVal, curObj, curIndex) => {
      if (selectedCarts[curIndex])
        return prevVal + (selectedQty[curIndex] || 1) * curObj.price
      return prevVal
    }, 0);

    if (selectedCoupon.type === 'percentage') {
      return Math.round(total * selectedCoupon.discount / 100);
    } else {
      return selectedCoupon.discount;
    }
  }

  console.log('cartData - ', cartData);

  console.log('selectedCarts - ', selectedCarts);

  console.log('selectedSize - ', selectedSize);

  console.log('selectedQty - ', selectedQty);

  useEffect(() => {
    fetchCarts()
  }, [])

  // Update dropdown positions on scroll and resize
  useEffect(() => {
    const updateDropdownPositions = () => {
      sizeDropdowns.forEach((isOpen, index) => {
        if (isOpen) {
          const button = document.querySelector(`.cart-select[data-dropdown-index="${index}"]`);
          if (button) {
            const rect = button.getBoundingClientRect();
            setDropdownPositions(prev => ({
              ...prev,
              [index]: {
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width
              }
            }));
          }
        }
      });
    };

    const handleScroll = () => {
      // Update positions when scrolling so dropdown follows the button
      updateDropdownPositions();
    };

    const handleResize = () => {
      updateDropdownPositions();
    };

    // Listen to scroll events on window and all scrollable containers
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', handleResize);
    
    // Also listen to scroll on the cart container
    const cartContainer = document.querySelector('.cart-page-container');
    if (cartContainer) {
      cartContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', handleResize);
      if (cartContainer) {
        cartContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [sizeDropdowns]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('.cart-select') || 
                                     event.target.closest('.dropdown-menu');
      
      if (!isClickInsideDropdown) {
        setSizeDropdowns(new Array(sizeDropdowns.length).fill(false));
        setQtyDropdowns(new Array(qtyDropdowns.length).fill(false));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sizeDropdowns.length, qtyDropdowns.length])
  return (
    <>
      <div className="cart-page-container">
        <div className="cart-header">
          <h1>Shopping Cart</h1>
          <p>Review your items and proceed to checkout</p>
        </div>

        <div className="cart-content" style={loading || cartData.length === 0 ? { gridTemplateColumns: '1fr' } : undefined}>
          <div className="cart-items-section">
          <div className="cartItemsList">
            {loading ? (
              <div className="cart-loading-placeholder" aria-hidden="true" />
            ) : cartData.length === 0 ? (
              <div className="empty-cart-container">
                <div className="empty-cart-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                </div>
                <h2 className="empty-cart-title">No items added in cart</h2>
                <p className="empty-cart-message">Your cart is empty. Start shopping to add items!</p>
                <button 
                  className="empty-cart-button"
                  onClick={() => navigate('/')}
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              cartData.map((cart, index) => {
              return (<div className="itemContainer-base-itemMargin">
                <div className="itemContainer-base-item">
                  <div className="itemContainer-base-itemLeft">
                    <div className={`itemComponents-base-selectionIconContainer ${selectedCarts[index] === true ? 'selected' : ''}`}>
                      <div className="itemComponents-base-animationContainer">
                        <img src={selectedCarts[index] === true ? selectSvg : unselectSvg} className="itemComponents-base-activeProduct" onClick={() => updateSelectedCarts(index)} />
                      </div>
                    </div>
                    <img src={cart?.urls?.[1] || cart?.urls?.[0] ? (String(cart.urls[1] || cart.urls[0]).startsWith('http') ? (cart.urls[1] || cart.urls[0]) : `${API_BASE_URL}/${(cart.urls[1] || cart.urls[0]).replace(/^\//, '')}`) : ''} alt="" />
                  </div>

                  <div className="itemContainer-base-itemRight">
                    <button 
                      className="cart-item-delete-btn"
                      onClick={() => setConfirmRemoveIndex(index)}
                      title="Remove item from cart"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <div className="brand-and-seller">
                      <div>{cart?.brand}</div>
                      <a>{cart?.title}</a>
                    </div>

                    <div className="itemComponents-base-sellerContainer">
                      <div className="itemComponents-base-sellerData">
                        Sold by: Puma Sports India Pvt. Ltd.(NSCM)
                      </div>
                    </div>

                    <div className="itemContainer-base-price">
                      <div>
                        <img src={rupeesIcon} />
                        {cart?.price}
                      </div>

                      <div>
                        <span>{cart?.mrp}</span>

                        <span>60% OFF</span>
                      </div>
                    </div>

                    <div className="itemContainer-base-sizeAndQtyContainer">
                      <div className="itemContainer-base-sizeAndQty">
                        {/*                         <div className="itemComponents-base-size">
                          <span>
                            <div>Size: </div>
                            <div className="cart-select">{selectedSize?.[index] || cart?.selectedSize}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="6"
                                height="3"
                                viewBox="0 0 6 3"
                                stroke="currentColor" fill="currentColor"
                                class="itemComponents-base-dropDown"
                              >
                                <path fill-rule="evenodd" d="M0 0h6L3 3z"></path>
                              </svg>
                            </div>
                            {sizeDropdowns?.[index] && (
                              <ul className="dropdown-menu">
                                {availableSizes?.[index]?.map((size, sizeIndex) => (
                                  <li
                                    key={sizeIndex}
                                    className="dropdown-item"
                                    onClick={() => handleSizeOrQtySelect(size, index, "SIZE")}
                                  >
                                    {size}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </span>
                        </div> */}

                        <div className="itemComponents-base-size">
                          <span>
                            <div>Size:</div>
                            <div className="size-select-wrapper">
                              <div
                                className={`cart-select ${sizeDropdowns?.[index] ? 'open' : ''}`}
                                data-dropdown-index={index}
                                onClick={(e) => handleDropdownToggle(index, "SIZE", e)}
                              >
                                {selectedSize?.[index] || 'Select'}
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="8"
                                  height="6"
                                  viewBox="0 0 8 6"
                                  fill="none"
                                  className={`itemComponents-base-dropDown ${sizeDropdowns?.[index] ? 'open' : ''}`}
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M0 0L4 6L8 0H0Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </div>

                              {sizeDropdowns?.[index] && dropdownPositions[index] && (
                                <ul 
                                  className="dropdown-menu"
                                  style={{
                                    position: 'fixed',
                                    top: `${dropdownPositions[index].top}px`,
                                    left: `${dropdownPositions[index].left}px`,
                                    minWidth: `${dropdownPositions[index].width}px`
                                  }}
                                >
                                  {availableSizes?.[index]?.map((size, sizeIndex) => (
                                    <li
                                      key={sizeIndex}
                                      className={`dropdown-item ${selectedSize?.[index] === size ? 'selected' : ''}`}
                                      onClick={() => handleSizeOrQtySelect(size, index, "SIZE")}
                                    >
                                      {size}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </span>
                        </div>

                        {/* <div className="itemComponents-base-quantity">
                          <span>Qty: {cart?.selectedQty}</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="6"
                            height="3"
                            viewBox="0 0 6 3"
                            class="itemComponents-base-dropDown"
                          >
                            <path fill-rule="evenodd" d="M0 0h6L3 3z"></path>
                          </svg>
                        </div> */}


                        <div className="itemComponents-base-size">
                          <span>
                            <div>Qty:</div>
                            <div className="quantity-controls">
                              <button 
                                className="quantity-btn minus-btn"
                                onClick={() => handleMinusClick(index)}
                              >
                                -
                              </button>
                              <span className="quantity-value">{selectedQty?.[index]}</span>
                              <button 
                                className="quantity-btn plus-btn"
                                onClick={() => handleQuantityChange(index, 1)}
                                disabled={(selectedQty?.[index] || 1) >= getCartLineMaxStock(cart, selectedSize?.[index] ?? cart?.selectedSize)}
                              >
                                +
                              </button>
                            </div>
                          </span>
                        </div>


                      </div>
                    </div>



                    <div className="returnPeriod-base-returnItem return-delivery-row">
                      <div className="returnPeriod-base-returnItem">
                        <div className="returnPeriod-base-returnIcon">
                          <img src={returnIcon} />
                        </div>
                        <div className="returnPeriod-base-returnText">
                          <span>14 days </span>
                          return available
                        </div>
                      </div>

                      <div className="returnPeriod-base-returnItem">
                        <div className="returnPeriod-base-returnIcon">
                          <img src={deliveryIcon} />
                        </div>
                        <div className="returnPeriod-base-returnText">
                          Delivery in
                          <span> 2 days </span>
                        </div>
                      </div>
                    </div>

                    {/* <div className="itemComponents-base-deliveryDateContainer">
                      <div class="itemComponents-base-messageText itemComponents-base-message">
                        <span>Delivery in </span>
                        <span class="itemComponents-base-highlightedMessage ">
                          2 days
                        </span>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
              )
              })
            )}

            {/* <div className="itemContainer-base-itemMargin">
              <div className="itemContainer-base-item">
                <div className="itemContainer-base-itemLeft">
                <div className="itemComponents-base-selectionIconContainer">
                      <div className="itemComponents-base-animationContainer">
                        <img src={unselectSvg} className="itemComponents-base-activeProduct "/>
                      </div>
                    </div>
                  <img src={prod1} />
                </div>

                <div className="itemContainer-base-itemRight">
                  <div className="brand-and-seller">
                    <div>Puma</div>
                    <a>Ess Polo T-shirt</a>
                  </div>

                  <div className="itemComponents-base-sellerContainer">
                    <div className="itemComponents-base-sellerData">
                      Sold by: Puma Sports India Pvt. Ltd.(NSCM)
                    </div>
                  </div>

                  <div className="itemContainer-base-sizeAndQtyContainer">
                    <div className="itemContainer-base-sizeAndQty">
                      <div className="itemComponents-base-size">
                        <span>Size: M</span>
                      </div>

                      <div className="itemComponents-base-quantity">
                        <span>Qty: 1</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="6"
                          height="3"
                          viewBox="0 0 6 3"
                          class="itemComponents-base-dropDown"
                        >
                          <path fill-rule="evenodd" d="M0 0h6L3 3z"></path>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="itemContainer-base-price">
                    <div>
                      <img src={rupeesIcon} />
                      679
                    </div>

                    <div>
                      <span>1,699</span>

                      <span>60% OFF</span>
                    </div>
                  </div>

                  <div className="returnPeriod-base-returnItem">
                    <div className="returnPeriod-base-returnIcon">
                      <img src={returnIcon} />
                    </div>
                    <div className="returnPeriod-base-returnText">
                      <span>14 days </span>
                      return available
                    </div>
                  </div>

                  <div className="itemComponents-base-deliveryDateContainer">
                    <div class="itemComponents-base-messageText itemComponents-base-message">
                      <span>Delivery in </span>
                      <span class="itemComponents-base-highlightedMessage ">
                        2 days
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="itemContainer-base-itemMargin">
              <div className="itemContainer-base-item">
                <div className="itemContainer-base-itemLeft">
                  <img src={prod1} />
                </div>

                <div className="itemContainer-base-itemRight">
                  <div className="brand-and-seller">
                    <div>Puma</div>
                    <a>Ess Polo T-shirt</a>
                  </div>

                  <div className="itemComponents-base-sellerContainer">
                    <div className="itemComponents-base-sellerData">
                      Sold by: Puma Sports India Pvt. Ltd.(NSCM)
                    </div>
                  </div>

                  <div className="itemContainer-base-sizeAndQtyContainer">
                    <div className="itemContainer-base-sizeAndQty">
                      <div className="itemComponents-base-size">
                        <span>Size: M</span>
                      </div>

                      <div className="itemComponents-base-quantity">
                        <span>Qty: 1</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="6"
                          height="3"
                          viewBox="0 0 6 3"
                          class="itemComponents-base-dropDown"
                        >
                          <path fill-rule="evenodd" d="M0 0h6L3 3z"></path>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="itemContainer-base-price">
                    <div>
                      <img src={rupeesIcon} />
                      679
                    </div>

                    <div>
                      <span>1,699</span>

                      <span>60% OFF</span>
                    </div>
                  </div>

                  <div className="returnPeriod-base-returnItem">
                    <div className="returnPeriod-base-returnIcon">
                      <img src={returnIcon} />
                    </div>
                    <div className="returnPeriod-base-returnText">
                      <span>14 days </span>
                      return available
                    </div>
                  </div>

                  <div className="itemComponents-base-deliveryDateContainer">
                    <div class="itemComponents-base-messageText itemComponents-base-message">
                      <span>Delivery in </span>
                      <span class="itemComponents-base-highlightedMessage ">
                        2 days
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {cartData.length > 0 && (
        <div className="cart-summary-section">
          <div className="cart-summary-card">
            <h2>Price Summary</h2>
            
            <div className="cart-summary-totals">
              <div className="summary-row">
                <span className="summary-label">Total MRP</span>
                <span className="summary-value">
                  ₹{cartData.reduce((prevVal, curObj, curIndex) => {
                    if (selectedCarts[curIndex])
                      return prevVal + (selectedQty[curIndex] || 1) * curObj.mrp
                    return prevVal
                  }, 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="summary-row discount">
                <span className="summary-label">Discount</span>
                <span className="summary-value discount">
                  - ₹{cartData.reduce((prevVal, curObj, curIndex) => {
                    if (selectedCarts[curIndex])
                      return prevVal + (selectedQty[curIndex] || 1) * (curObj.mrp - curObj.price)
                    return prevVal
                  }, 0).toLocaleString('en-IN')}
                </span>
              </div>
              
              {/* Item Breakdown Section */}
              {cartData.filter((cart, index) => selectedCarts[index]).length > 0 && (
                <>
                  <div className="summary-divider"></div>
                  <div className="cart-items-breakdown">
                    <div className="breakdown-header">
                      <span className="breakdown-title">Items ({cartData.reduce((sum, cart, index) => {
                        if (selectedCarts[index]) return sum + (selectedQty[index] || 1);
                        return sum;
                      }, 0)} items)</span>
                    </div>
                    <div className="breakdown-items">
                      {cartData.map((cart, index) => {
                        if (!selectedCarts[index]) return null;
                        const qty = selectedQty[index] || 1;
                        const size = selectedSize[index] || cart.selectedSize;
                        const unitPrice = cart.price;
                        const totalPrice = unitPrice * qty;
                        
                        return (
                          <div key={index} className="breakdown-item">
                            <div className="breakdown-item-left">
                              <div className="breakdown-item-name">
                                {cart?.title?.length > 35 ? `${cart.title.substring(0, 35)}...` : cart?.title || 'Product'}
                              </div>
                              <div className="breakdown-item-meta">
                                <span className="breakdown-size">Size: {size}</span>
                                <span className="breakdown-qty">Qty: {qty}</span>
                                <span className="breakdown-unit-price">@ ₹{unitPrice.toLocaleString('en-IN')}</span>
                              </div>
                            </div>
                            <div className="breakdown-item-right">
                              <span className="breakdown-item-total">₹{totalPrice.toLocaleString('en-IN')}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="summary-divider"></div>
                </>
              )}
              
              <div className="summary-row coupon-row">
                <span className="summary-label">Coupon</span>
                <div className="coupon-actions">
                  {selectedCoupon ? (
                    <div className="applied-coupon">
                      <span className="coupon-code">{selectedCoupon.code}</span>
                      <button 
                        className="remove-coupon-btn"
                        onClick={handleRemoveCoupon}
                        title="Remove coupon"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button 
                      className="avail-coupon-btn"
                      onClick={() => setShowCouponModal(true)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      Avail Coupon
                    </button>
                  )}
                </div>
              </div>
              {selectedCoupon && (
                <div className="summary-row discount">
                  <span className="summary-label">Coupon Discount</span>
                  <span className="summary-value discount">
                    - ₹{couponDiscount().toLocaleString('en-IN')}
                  </span>
                </div>
              )}
              {/* <div class="priceDetail-base-row ">
                <span class=" ">
                  Platform Fee
                  <span class="priceDetail-base-knowMore ">Know More</span>
                </span>
                <span class="priceDetail-base-value">
                  <span class="priceDetail-base-discount">FREE</span>
                </span>
              </div> */}

              <div className="summary-row shipping">
                <span className="summary-label">Shipping</span>
                <span className="summary-value free">FREE</span>
              </div>
              <div className="summary-divider"></div>
              <div className="summary-row total">
                <span className="summary-label">Total Amount</span>
                <span className="summary-value total">
                  ₹{(cartData.reduce((prevVal, curObj, curIndex) => {
                    if (selectedCarts[curIndex])
                      return prevVal + (selectedQty[curIndex] || 1) * curObj.price
                    return prevVal
                  }, 0) - couponDiscount()).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            <div className="place-order-footer">
              <button 
                className="place-order-btn"
                onClick={placeOrder}
                disabled={loading || placingOrder || cartData.length === 0}
              >
                {placingOrder ? 'PROCESSING...' : 'PLACE ORDER'}
              </button>
            </div>
          </div>
          </div>
        )}
      </div>
    </div>

      {confirmRemoveIndex !== null && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999}}>
          <div style={{background: '#fff', borderRadius: 8, width: 360, maxWidth: '90%', padding: 20, boxShadow: '0 10px 30px rgba(0,0,0,0.2)'}}>
            <div style={{fontSize: 16, fontWeight: 600, marginBottom: 10}}>Remove item?</div>
            <div style={{fontSize: 14, color: '#444', marginBottom: 20}}>Quantity would drop below 1. Do you want to remove this product from your cart?</div>
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: 12}}>
              <button onClick={() => setConfirmRemoveIndex(null)} style={{padding: '8px 14px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer'}}>Cancel</button>
              <button onClick={() => removeCartItem(confirmRemoveIndex)} style={{padding: '8px 14px', borderRadius: 6, border: '1px solid #d32f2f', background: '#d32f2f', color: '#fff', cursor: 'pointer'}}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {loading && <PageLoadLoader />}
      {placingOrder && <PageLoadLoader />}

      {/* Coupon Modal */}
      {showCouponModal && (
        <div className="coupon-modal-overlay cart-coupon-modal" onClick={() => setShowCouponModal(false)}>
          <div className="coupon-modal" onClick={(e) => e.stopPropagation()}>
            <div className="coupon-modal-header">
              <h2>Available Coupons</h2>
              <button 
                className="close-modal-btn"
                onClick={() => setShowCouponModal(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="coupon-modal-content">
              <div className="coupons-list">
                {availableCoupons.map((coupon) => {
                  const totalAmount = cartData.reduce((prevVal, curObj, curIndex) => {
                    if (selectedCarts[curIndex])
                      return prevVal + (selectedQty[curIndex] || 1) * curObj.price
                    return prevVal
                  }, 0);
                  const isApplicable = totalAmount >= coupon.minOrder;
                  return (
                    <div 
                      key={coupon.id} 
                      className={`coupon-card ${!isApplicable ? 'disabled' : ''} ${selectedCoupon?.id === coupon.id ? 'selected' : ''}`}
                      onClick={() => isApplicable && handleCouponSelect(coupon)}
                    >
                      <div className="coupon-card-left">
                        <div className="coupon-discount">
                          {coupon.type === 'percentage' ? (
                            <span className="discount-percentage">{coupon.discount}%</span>
                          ) : (
                            <span className="discount-amount">₹{coupon.discount}</span>
                          )}
                          <span className="discount-off">OFF</span>
                        </div>
                        <div className="coupon-details">
                          <div className="coupon-code-text">{coupon.code}</div>
                          <div className="coupon-description">{coupon.description}</div>
                          <div className="coupon-min-order">
                            Min. order: ₹{coupon.minOrder.toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                      <div className="coupon-card-right">
                        {!isApplicable ? (
                          <div className="coupon-not-applicable">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M8 4v4m0 4h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            Not Applicable
                          </div>
                        ) : selectedCoupon?.id === coupon.id ? (
                          <div className="coupon-applied">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Applied
                          </div>
                        ) : (
                          <button className="apply-coupon-btn">
                            Apply
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
